import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Modal, Text } from '@reservamos/elements';
import mockupCelular from '../../../images/brands-unique/cds/mockup-celular.png';
import iconPhone from '../../../images/brands-unique/cds/icon-phone.svg';
import iconApprove from '../../../images/brands-unique/cds/icon-approve.svg';
import iconNumber from '../../../images/brands-unique/cds/icon-number.svg';
import iconClick from '../../../images/brands-unique/cds/icon-click.svg';

/**
 * StepItem component for displaying individual steps in the modal.
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.icon - The icon component to display
 * @param {string} props.text - The text content for the step
 * @param {string} props.highlightedText - The text to highlight (optional)
 */
const StepItem = ({ icon, text, highlightedText }) => {
  return (
    <div className="flex items-center gap-4">
      <div className="w-8 h-8 bg-[#9335ab] rounded-full flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div className="flex-1">
        <Text size="M" weight="normal" className="text-gray-800 font-sans">
          {text}
          {highlightedText && (
            <span style={{ color: '#9335ab', fontWeight: 600 }}> {highlightedText}</span>
          )}
        </Text>
      </div>
    </div>
  );
};

StepItem.propTypes = {
  icon: PropTypes.node.isRequired,
  text: PropTypes.string.isRequired,
  highlightedText: PropTypes.string,
};

/**
 * YapeInfoModal component.
 * Renders a modal with information about where to find the Yape approval code.
 * Shows step-by-step instructions with icons and an image placeholder.
 * @param {Object} props - Component props
 * @param {boolean} props.isVisible - Whether the modal should be visible
 * @param {Function} props.onClose - Function to call when modal is closed
 */
const YapeInfoModal = ({ isVisible, onClose }) => {
  const { t } = useTranslation('payment');

  // Handle closing the modal - notify parent
  const closeModal = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  if (!isVisible) return null;

  return (
    <Modal
      title={t('yape.info_modal.title')}
      responsiveSize="XXL"
      expandedModal
      closeOnOverlayClick
      onCloseModal={closeModal}
      className="[&_.modal-title]:text-lg relative [&_.modal-content]:!max-w-[95vw] [&_.modal-content]:!w-[95vw]"
    >
      {/* Desktop image - positioned at bottom left of modal */}
      <img
        src={mockupCelular}
        alt={t('yape.info_modal.image_placeholder')}
        className="absolute bottom-0 left-4 w-56 h-auto object-contain z-10 md:block hidden"
      />

      {/* Mobile image - positioned above modal using div strategy */}
      {/* We use different strategies for desktop (img) and mobile (div with backgroundImage) 
           because each approach is optimized for its specific positioning requirements */}
      <div
        className="absolute -top-80 left-1/2 transform -translate-x-1/3 w-56 h-64 bg-no-repeat bg-center bg-contain z-20 block md:hidden"
        style={{
          backgroundImage: `url(${mockupCelular})`,
        }}
        role="img"
        aria-label={t('yape.info_modal.image_placeholder')}
      />
      <div className="flex flex-col md:flex-row gap-4 px-4 pb-4">
        {/* Left side - Empty space for image */}
        <div className="flex-1" />

        {/* Right side - Steps */}
        <div className="flex-1">
          <div className="space-y-4">
            <StepItem
              icon={<img src={iconPhone} alt="Phone" className="w-4 h-4" />}
              text={t('yape.info_modal.step1')}
            />

            <StepItem
              icon={<img src={iconClick} alt="Click" className="w-4 h-4" />}
              text={t('yape.info_modal.step2_text')}
              highlightedText={t('yape.info_modal.step2_highlight')}
            />

            <StepItem
              icon={<img src={iconNumber} alt="Number" className="w-5 h-5" />}
              text={t('yape.info_modal.step3')}
            />

            <StepItem
              icon={<img src={iconApprove} alt="Approve" className="w-4 h-4" />}
              text={t('yape.info_modal.step4')}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

YapeInfoModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
};

export default YapeInfoModal;
